import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Bell,
  BookUser,
  ClipboardList,
  Crosshair,
  FolderOpen,
  Gem,
  Mail,
  Phone,
  SquarePen,
  StickyNote,
  Users,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import ACL_RELATIONSHIPS from "acl-relationships";
import CallsTable from "components/CallsTableV3/CallsTableV3";
import { linkedTabSections, useLinkedTables } from "components/linkedTables";
import { SECTION_FILL_CLASSES } from "components/sidebar/sidebarShell";
import _RevenueOpportunitiesTable from "components/RevenueOpportunitiesTableV3/RevenueOpportunitiesTableV3";
import { DetailBreadcrumb } from "components/sidebar/DetailBreadcrumb";
import SidebarDetailLayout from "components/SidebarDetailLayout";
import withRoleCheck from "components/hocs/withRoleCheck";
import AuditLog from "components/views/AuditLog";
import NotesView from "components/views/NotesView";
import CONFIG from "Config";
import { useAppTabs } from "hooks/useAppTabs";
import useAuthorized from "hooks/useAuthorized";
import usePageTitle from "hooks/usePageTitle";
import { useRuvixxFlags } from "providers/RuvixxFlagsProvider";
import CasesTable from "routes/case/CasesTableV3";
import ContactsTable from "routes/campaign/components/campaign_contact_table/CampaignContactsTableV3";
import CampaignInfoPanel from "routes/campaign/components/CampaignInfoPanel";
import CampaignTargetsTable from "routes/campaign/components/campaign_target_table/CampaignTargetTableV3";
import EmailSchedulesTable from "routes/campaign/components/email_schedule_table/EmailScheduleTableV3";
import NoticesTableV3 from "routes/campaign/components/notices_table/NoticesTableV3";
import { useCampaignNavigation } from "routes/campaign/useCampaignNavigation";
import DialSessionModal from "routes/dial_sessions/components/DialSessionModal";
import LeadsTable from "routes/leads/table/LeadsTableV3";
import AuthService from "services/Auth";
import CampaignService from "services/Campaign";

const RevenueOpportunitiesTable = withRoleCheck(
  _RevenueOpportunitiesTable,
  [ACL_RELATIONSHIPS.revenueOpportunity.readTable],
  { showMsg: true }
);

const CAN_DIAL_PERMISSIONS = [
  ACL_RELATIONSHIPS.dialSession.create,
  ACL_RELATIONSHIPS.voicemailForFilters.read,
  ACL_RELATIONSHIPS.emailUrlForms.read,
];

const initialCampaignData = {
  id: null,
  name: null,
  creator_user_id: null,
  assigned_user_id: null,
  assigned_user_full_name: null,
  info: { custom: null },
  created_at: null,
  metrics: {},
};

const fetchCampaignById = async ({ queryKey }) => {
  const [, campaignId] = queryKey;
  const campaign = await CampaignService.getCampaign(campaignId);
  return campaign;
};

const CampaignDetail = () => {
  const params = useParams();
  const {
    data: campaignData,
    refetch,
    isFetched: isCampaignFetched,
  } = useQuery({
    queryKey: ["campaign", params.id],
    queryFn: fetchCampaignById,
    initialData: initialCampaignData,
    enabled: AuthService.isLoggedIn() && !!params.id,
  });

  usePageTitle(campaignData.name || "Pleteo");

  const basePath = `/campaigns/${params.id}`;

  const canViewCampaignSchedules = useAuthorized([
    ACL_RELATIONSHIPS.campaignSchedule.read,
  ]);
  const canViewCampaignLeads = useAuthorized([
    ACL_RELATIONSHIPS.campaignLead.readTable,
  ]);
  const canDial = useAuthorized(CAN_DIAL_PERMISSIONS);
  const canEditCampaign = useAuthorized([ACL_RELATIONSHIPS.campaign.edit]);
  const { openEdit } = useCampaignNavigation();
  const { enable_leads_feature } = useRuvixxFlags();

  const { linkedTabs, isLoading: isLoadingLinkedTabs } = useLinkedTables(
    "Campaign",
    params.id
  );

  const [auditLogId, setAuditLogId] = useState(null);
  const tabs = useAppTabs({
    basePath,
    defaultTab: "activityLog",
  });
  const { activeTab, handleTabChange: onTabChange } = tabs;

  const handleTabChange = useCallback(
    (_e, { name, auditLogId = null }) => {
      setAuditLogId(auditLogId);
      onTabChange(undefined, { name });
    },
    [onTabChange]
  );

  // The Activity Log deep-link (set by a note's "See Original") applies once.
  // Clear it when the active section changes away from the Activity Log so a
  // later return shows the newest page, not the old entry.
  const [lastSection, setLastSection] = useState(activeTab);
  if (activeTab !== lastSection) {
    setLastSection(activeTab);
    if (activeTab !== "activityLog") {
      setAuditLogId(null);
    }
  }

  const sections = useMemo(() => {
    const items = [
      {
        key: "activityLog",
        label: "Activity Log",
        icon: ClipboardList,
        content: (
          <AuditLog
            modelId={params.id}
            modelType="Campaign"
            auditLogId={auditLogId}
          />
        ),
      },
      {
        key: "targetCompanies",
        label: "Target Companies",
        icon: Crosshair,
        content: (
          <div className={SECTION_FILL_CLASSES}>
            <CampaignTargetsTable
              campaign={campaignData}
              campaignId={params.id}
            />
          </div>
        ),
      },
      {
        key: "activity",
        label: "Notes",
        icon: StickyNote,
        content: (
          <NotesView
            modelType="Campaign"
            modelId={params.id}
            handleTabChange={handleTabChange}
          />
        ),
      },
      {
        key: "contacts",
        label: "Contacts",
        icon: Users,
        content: (
          <div className={SECTION_FILL_CLASSES}>
            <ContactsTable
              tableName="campaign_contacts"
              campaignId={parseInt(params.id)}
            />
          </div>
        ),
      },
      {
        key: "leads",
        label: "Leads",
        icon: BookUser,
        hidden: !canViewCampaignLeads || !enable_leads_feature,
        content: (
          <div className={SECTION_FILL_CLASSES}>
            <LeadsTable scope="campaign" id={parseInt(params.id)} />
          </div>
        ),
      },
      {
        key: "notices",
        label: "Notices",
        icon: Bell,
        content: (
          <div className={SECTION_FILL_CLASSES}>
            <NoticesTableV3 campaignId={params.id} />
          </div>
        ),
      },
      {
        key: "emailSchedules",
        label: "Email Schedules",
        icon: Mail,
        hidden: !canViewCampaignSchedules,
        content: (
          <div className={SECTION_FILL_CLASSES}>
            <EmailSchedulesTable campaignId={params.id} />
          </div>
        ),
      },
      {
        key: "calls",
        label: "Calls",
        icon: Phone,
        hidden: !CONFIG.IS_PHONE_ENABLED,
        content: (
          <div className={SECTION_FILL_CLASSES}>
            <CallsTable campaignId={params.id} />
          </div>
        ),
      },
      {
        key: "cases",
        label: "Cases",
        icon: FolderOpen,
        hidden: !CONFIG.ENABLE_PIRACY,
        content: (
          <div className={SECTION_FILL_CLASSES}>
            <CasesTable campaignId={params.id} />
          </div>
        ),
      },
      {
        key: "revenueOpportunities",
        label: "Revenue Opportunities",
        icon: Gem,
        content: (
          <div className={SECTION_FILL_CLASSES}>
            <RevenueOpportunitiesTable campaignId={params.id} />
          </div>
        ),
      },
    ];
    items.push(...linkedTabSections(linkedTabs, params.id));
    return items;
  }, [
    campaignData,
    params.id,
    auditLogId,
    handleTabChange,
    canViewCampaignLeads,
    canViewCampaignSchedules,
    enable_leads_feature,
    linkedTabs,
  ]);

  if (!campaignData.id) {
    return null;
  }

  return (
    <SidebarDetailLayout
      sections={sections}
      tabs={tabs}
      sectionsLoading={isLoadingLinkedTabs}
      header="Campaign"
      breadcrumb={
        <DetailBreadcrumb
          parentTo="/campaigns"
          parentLabel="All Campaigns"
          current={campaignData.name}
        />
      }
      title={campaignData.name}
      titleActions={
        <>
          {CONFIG.IS_PHONE_ENABLED && canDial && (
            <DialSessionModal
              campaignId={parseInt(params.id)}
              onSuccess={refetch}
              trigger={
                <Button variant="outline" size="sm">
                  <Phone data-icon="inline-start" />
                  Dial
                </Button>
              }
            />
          )}
          {canEditCampaign && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => openEdit(params.id)}
            >
              <SquarePen data-icon="inline-start" />
              Edit
            </Button>
          )}
        </>
      }
      info={
        <CampaignInfoPanel
          campaign={campaignData}
          isLoading={!isCampaignFetched}
        />
      }
      infoLabel="Campaign Details"
    />
  );
};

export default CampaignDetail;
